/**
 * Centralized, code-level entity content overrides for local debugging.
 *
 * The single place to override any entity's content on the client. Every entity fetched from the
 * server — via GET/`_mget` and via ES `query` — funnels through `Store.materializeRecord`, which
 * looks up an override here (keyed by the entity's `uniqueId`) and REPLACES the server content with
 * it wholesale before the entity is materialized (the entity's `uniqueId` is preserved). Nothing is
 * ever written back to the server, so you can test how the app renders/behaves against edited
 * content without touching a single row.
 *
 * Each entry is the FULL replacement content. For a schema entity, the local schema file is the
 * content, so require it directly:
 *
 *   export const ENTITY_DEBUG_OVERRIDES = {
 *     '67cac340-...': require('../../../schemas/entities/imports/sftp/sftpIngestConfig.json'),
 *   }
 *
 * Keep this map EMPTY on commit — it's a scratchpad for local work, not config that should ship.
 */

/**
 * uniqueId -> full replacement content. Add entries here to override entities locally.
 * Keep empty when committing.
 */
// [VD-24597] LOCAL ONLY — preview the new ui:formWizard creation flow against a deployment whose
// schema predates it. The override replaces each schema entity wholesale with the local schema
// file, which now carries the new uiSchema.web.entityCreationPanel (ui:expandablePanel ->
// ui:formWizard). The file already holds uniqueId/mixins/properties/uiSchema (the store re-adds the
// server uniqueId). Revert before merge.
/* eslint-disable @typescript-eslint/no-var-requires */
const SFTP_INGEST_CONFIG_SCHEMA = require('../../../schemas/entities/imports/sftp/sftpIngestConfig.json')
const MULTI_ENTITY_PAYLOAD_CONFIG_SCHEMA = require('../../../schemas/entities/imports/multientity/multiEntityIngestionPayloadConfig.json')
/* eslint-enable @typescript-eslint/no-var-requires */

export const ENTITY_DEBUG_OVERRIDES: Record<string, any> = {
  '67cac340-2662-4184-a3c1-b7a3251d7d68': SFTP_INGEST_CONFIG_SCHEMA,
  '62ed12d4-0419-11f0-a333-f783787e5a87': MULTI_ENTITY_PAYLOAD_CONFIG_SCHEMA,
}

// Parked scratchpad (inactive — the `_X` suffix keeps it out of the active map above). NOTE: these
// were authored as partial merges; under the replace-only mechanism each would need to be FULL
// entity content before use.
export const ENTITY_DEBUG_OVERRIDES_X: Record<string, any> = {

  // [VD-24632] Local preview of the "Ingestion Configs" view filters before the
  // schema change (schemas/data/views/pages/internalTools/ingestionPipelineConfigs.json)
  // is deployed. Merges the expanded availableFilters onto whatever the server returns.
  'a1b2c3d4-e5f6-7890-abcd-ef1234567890': {
    view: {
      uiSchema: {
        detailPanelDefaultSize: 544,
        addEntityDefaultValue: {
          core_integrations_sftp_ingestion_config: {
            messageType: 'MULTI_ENTITY',
            shouldPreProcessPayload: true,
          },
        },
      },
      state: {
        viewMode: 'listDetail',
        availableFilters: [
          { path: 'precomputation.displayName', label: 'Name', type: 'match' },
          {
            path: 'core_integrations_sftp_ingestion_config.messageType',
            label: 'Message Type',
            type: 'match',
          },
          {
            path: 'core_integrations_sftp_ingestion_config.sourceDirectory',
            label: 'Source Directory',
            type: 'contains',
          },
          {
            path: 'core_integrations_sftp_ingestion_config.firm',
            label: 'Firm',
            type: 'matchEdge',
          },
          { path: 'creationDate', label: 'Creation Date', type: 'range' },
        ],
      },
    },
  },

  // [VD-24637] Local preview of the FTP Connection detail section before the schema's
  // uiSchema change is deployed. Merges uiSchema onto the FTP connection schema entity so the
  // detail panel renders when a connection is opened (e.g. navigate to /entity/<ftpConnectionId>).
  'adcc9fda-1b56-4704-9144-2b81a2aa6e26': {
    metadata: { isCloneable: true },
    uiSchema: {
      web: {
        entityDetailSection: {
          type: 'ui:view',
          children: [
            {
              type: 'ui:section',
              title: 'Connection',
              isCollapsable: true,
              children: [
                {
                  type: 'ui:formTable',
                  className: 'u-bumperBottom',
                  children: [
                    {
                      label: 'Name',
                      type: 'ui:inputField',
                      value: 'core_integrations_ftp_connection.name',
                      placeholder: 'Display name for this connection',
                    },
                    {
                      label: 'Description',
                      type: 'ui:inputField',
                      value: 'core_integrations_ftp_connection.description',
                    },
                    {
                      label: 'Protocol',
                      type: 'ui:selectField',
                      value: 'core_integrations_ftp_connection.protocol',
                    },
                    {
                      label: 'Host',
                      type: 'ui:inputField',
                      value: 'core_integrations_ftp_connection.host',
                      placeholder: 'e.g. sftp.example.com',
                    },
                    {
                      label: 'Port',
                      type: 'ui:inputField',
                      value: 'core_integrations_ftp_connection.port',
                      placeholder: 'e.g. 22',
                    },
                    {
                      label: 'Username',
                      type: 'ui:inputField',
                      value: 'core_integrations_ftp_connection.username',
                    },
                    {
                      label: 'Source Directory',
                      type: 'ui:inputField',
                      value: 'core_integrations_ftp_connection.sourceDirectoryOverride',
                      placeholder: 'Optional path override',
                    },
                  ],
                },
              ],
            },
            {
              type: 'ui:section',
              title: 'Credentials',
              isCollapsable: true,
              children: [
                {
                  type: 'ui:helpBlock',
                  helpText:
                    'Provide a password or an RSA private key. Secrets are masked and write-only.',
                  className: 'u-bumperBottom--sm',
                },
                {
                  type: 'ui:formTable',
                  className: 'u-bumperBottom',
                  children: [
                    {
                      label: 'Password',
                      type: 'ui:secretStringField',
                      value: 'core_integrations_ftp_connection.password',
                    },
                    {
                      label: 'RSA Key',
                      type: 'ui:secretStringField',
                      value: 'core_integrations_ftp_connection.rsaKey',
                    },
                  ],
                },
              ],
            },
          ],
        },
        entityCreationPanel: {
          type: 'ui:panel',
          title: 'New FTP Connection',
          size: 'sm',
          children: [
            {
              $ref: '#/uiSchema/web/entityDetailSection',
            },
          ],
          mapActionsToProps: {
            onClose: 'handleOnClose',
          },
          panelFooter: {
            type: 'ui:entityFooter',
            mapActionsToProps: {
              onCancelButtonClick: 'handleOnClose',
              onPrimaryButtonClick: 'handleOnSave',
            },
            mapStateToProps: {
              entity: 'entity',
              errors: 'errors',
            },
          },
        },
      },
    },
  },

  // [VD-24633] Local preview of the SFTP Ingestion Config detail section before the
  // schema's uiSchema change is deployed. Merges uiSchema onto the schema entity so the
  // detail panel renders when a config is opened.
  '67cac340-2662-4184-a3c1-b7a3251d7d68': {
    metadata: { isCloneable: true },
    uiSchema: {
      web: {
        entitySelectOptionCard: {
          type: 'ui:entityPreviewCard',
          data: [
            { type: 'identifier', formula: 'core_integrations_sftp_ingestion_config.firm.displayName' },
            { type: 'status', color: 'default', path: 'core_integrations_sftp_ingestion_config.messageType' },
            {
              type: 'status',
              color: 'blue',
              label: 'Dir',
              path: 'core_integrations_sftp_ingestion_config.sourceDirectory',
            },
            { type: 'status', color: 'default', label: 'ID', path: 'uniqueId' },
          ],
        },
        entitySelectDisplayCard: {
          type: 'ui:entityPreviewCard',
          data: [
            { type: 'identifier', formula: 'core_integrations_sftp_ingestion_config.firm.displayName' },
            { type: 'status', color: 'default', path: 'core_integrations_sftp_ingestion_config.messageType' },
            {
              type: 'status',
              color: 'blue',
              label: 'Dir',
              path: 'core_integrations_sftp_ingestion_config.sourceDirectory',
            },
            { type: 'status', color: 'default', label: 'ID', path: 'uniqueId' },
          ],
        },
        entityDetailSection: {
          type: 'ui:view',
          children: [
            {
              type: 'ui:section',
              title: 'Pipeline',
              isCollapsable: true,
              children: [
                {
                  type: 'ui:formTable',
                  className: 'u-bumperBottom',
                  children: [
                    {
                      label: 'Message Type',
                      type: 'ui:selectField',
                      value: 'core_integrations_sftp_ingestion_config.messageType',
                    },
                    {
                      label: 'Firm',
                      type: 'ui:entitySelectField',
                      entityType: '/1.0/entities/metadata/firm.json',
                      value: 'core_integrations_sftp_ingestion_config.firm',
                    },
                    {
                      label: 'Source Directory',
                      type: 'ui:inputField',
                      value: 'core_integrations_sftp_ingestion_config.sourceDirectory',
                      placeholder: 'Path on the FTP server to poll',
                    },
                    {
                      label: 'Payload Config',
                      type: 'ui:entitySelectField',
                      entityType:
                        '/1.0/entities/metadata/core_integrations_ingestion_multiEntity_payloadConfig.json',
                      value: 'core_integrations_sftp_ingestion_config.multiEntityPayloadConfig',
                    },
                    {
                      label: 'Pre-Process Payload',
                      type: 'ui:checkboxField',
                      value: 'core_integrations_sftp_ingestion_config.shouldPreProcessPayload',
                    },
                  ],
                },
              ],
            },
            {
              type: 'ui:section',
              title: 'FTP Connections',
              isCollapsable: true,
              children: [
                {
                  type: 'ui:helpBlock',
                  helpText:
                    'FTP Connection entity id per environment. Ids differ per environment and are not resolved or validated here. A job with no entry for the current environment will not start.',
                  className: 'u-bumperBottom--sm',
                },
                {
                  type: 'ui:dictionary',
                  value: 'core_integrations_sftp_ingestion_config.srcFtpConnectionMap',
                  keyLabel: 'Environment',
                  valueLabel: 'FTP Connection ID',
                  addButtonText: 'Add Environment',
                  items: {
                    type: 'ui:formTable',
                    className: 'c-dictionaryEntry',
                    children: [
                      {
                        type: 'ui:dictionaryKey',
                        style: { width: 200 },
                        itemTemplate: {
                          type: 'ui:selectField',
                          placeholder: 'Environment (e.g. staging)',
                        },
                      },
                      {
                        type: 'ui:dictionaryValue',
                        className: 'c-dictionaryValue',
                        itemTemplate: {
                          type: 'ui:input',
                          value: 'entityId',
                          isHorizontalLayout: false,
                          placeholder: 'FTP Connection entity id',
                        },
                      },
                    ],
                  },
                },
              ],
            },
            {
              type: 'ui:section',
              title: 'Polling & Ordering',
              isCollapsable: true,
              children: [
                {
                  type: 'ui:formTable',
                  className: 'u-bumperBottom',
                  children: [
                    {
                      label: 'Poll Interval (seconds)',
                      type: 'ui:inputField',
                      value: 'core_integrations_sftp_ingestion_config.period',
                      placeholder: 'e.g. 60',
                    },
                    {
                      label: 'Scan Recursively',
                      type: 'ui:checkboxField',
                      value: 'core_integrations_sftp_ingestion_config.shouldScanRecursively',
                    },
                    {
                      label: 'Max Payloads Per Run',
                      type: 'ui:inputField',
                      value: 'core_integrations_sftp_ingestion_config.maxPayloadsPerRun',
                    },
                    {
                      label: 'Order Key',
                      type: 'ui:selectField',
                      value: 'core_integrations_sftp_ingestion_config.ordering.orderKey',
                    },
                    {
                      label: 'Order',
                      type: 'ui:selectField',
                      value: 'core_integrations_sftp_ingestion_config.ordering.order',
                    },
                    {
                      label: 'Backup Behavior',
                      type: 'ui:selectField',
                      value: 'core_integrations_sftp_ingestion_config.ordering.backupBehavior',
                    },
                    {
                      label: 'Filename Regex',
                      type: 'ui:inputField',
                      value: 'core_integrations_sftp_ingestion_config.filenameRegex',
                      placeholder: 'Required when ordering by datetime',
                    },
                    {
                      label: 'Datetime Pattern',
                      type: 'ui:inputField',
                      value: 'core_integrations_sftp_ingestion_config.datetimePattern',
                      placeholder: 'Required when ordering by datetime',
                    },
                  ],
                },
              ],
            },
            {
              type: 'ui:section',
              title: 'Retry & Notifications',
              isCollapsable: true,
              children: [
                {
                  type: 'ui:helpBlock',
                  helpText:
                    'Retries cover system-level failures only (e.g. 423), not payload parsing or ingestion errors.',
                  className: 'u-bumperBottom--sm',
                },
                {
                  type: 'ui:formTable',
                  className: 'u-bumperBottom',
                  children: [
                    {
                      label: 'Retry On Failure',
                      type: 'ui:checkboxField',
                      value:
                        'core_integrations_sftp_ingestion_config.ingestionRetryBehavior.shouldRetry',
                    },
                    {
                      label: 'Retry Attempts',
                      type: 'ui:inputField',
                      value:
                        'core_integrations_sftp_ingestion_config.ingestionRetryBehavior.numAttempts',
                    },
                    {
                      label: 'Email Ingestion Enabled',
                      type: 'ui:checkboxField',
                      value: 'core_integrations_sftp_ingestion_config.email.enabled',
                    },
                    {
                      label: 'Mailgun Prefix',
                      type: 'ui:inputField',
                      value: 'core_integrations_sftp_ingestion_config.email.mailgunPrefix',
                      placeholder: 'Payloads to <prefix>@mg.withvector.com trigger ingestion',
                    },
                  ],
                },
              ],
            },
          ],
        },
        entityCreationPanel: {
          type: 'ui:panel',
          title: 'New SFTP Ingestion Config',
          size: 'sm',
          children: [
            {
              $ref: '#/uiSchema/web/entityDetailSection',
            },
          ],
          mapActionsToProps: {
            onClose: 'handleOnClose',
          },
          panelFooter: {
            type: 'ui:entityFooter',
            mapActionsToProps: {
              onCancelButtonClick: 'handleOnClose',
              onPrimaryButtonClick: 'handleOnSave',
            },
            mapStateToProps: {
              entity: 'entity',
              errors: 'errors',
            },
          },
        },
      },
    },
  },
}
